import { createTheme, lighten } from '@mui/material/styles'
import type { ThemeMode } from './themeMode'

const HEX = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/
// Farm-fresh terracotta — the storefront's default brand when a shop hasn't set one.
const DEFAULT_BRAND = '#BF5A2B'

/*
 * The storefront design language in TWO modes (redesign, Aug 2026):
 *   light = "Sunny Chick"     — warm cream + egg-yolk gold, rounded feather-cut buttons.
 *   dark  = "Midnight Rooster" — charcoal canvas + gold accents, crisp squared buttons.
 * These tokens are the app's OWN look — shared across every shop, NOT tenant data. The single
 * per-tenant input is `primary` (the shop's brand_color), which tints the CTAs/accents in BOTH
 * modes; the mode only swaps the neutral canvas (cream ⇄ charcoal). Fonts are self-hosted in
 * shared/fonts.css (imported in main.tsx) — no CDN.
 */
const YOLK = '#EAA62F' // egg-yolk gold — secondary accents / "fresh" pills, the gold in both modes
const OLIVE = '#7A8A3D' // fresh green — the success / "NEW" status pill
const SLAB = '"Zilla Slab", "Georgia", serif'
// The redesign's "feather-cut" corner (light mode) — one squared corner on every notched shape.
const FEATHER = '12px 12px 12px 4px'
const FEATHER_LG = '16px 16px 16px 6px'

// Sunny Chick (light) neutrals — warmer than a plain cream so the yellow-bordered cards pop off it.
const LIGHT = {
  bg: '#FFF6D8', // page background (warm buttermilk — matches the Sunny Chick mockup)
  paper: '#FFFCF0', // cards / surfaces (warm cream)
  text: '#3A2416', // primary text (dark brown)
  text2: '#8A6F56', // secondary text (muted brown)
  border: '#FCE3A0', // egg-yolk card border — the mockup's defining warm outline
  divider: 'rgba(59,42,30,0.12)',
  shadow: '0 6px 14px -8px rgba(220,150,30,0.45), 0 2px 6px rgba(220,150,30,0.12)',
  btn: FEATHER,
  btnLg: FEATHER_LG,
}
// Midnight Rooster (dark) neutrals.
const DARK = {
  bg: '#17110B', // page background (near-black warm charcoal)
  paper: '#241A13', // cards / surfaces
  text: '#F6EAD2', // primary text (cream)
  text2: '#B0895A', // secondary text (muted gold-brown)
  border: '#3D2E20', // hairline card border
  divider: '#3D2E20',
  shadow: 'none', // dark surfaces read by border, not shadow (like the mockup)
  btn: '8px', // crisp squared premium buttons
  btnLg: '10px',
}

export function buildTheme(brandColor?: string | null, mode: ThemeMode = 'light') {
  const t = mode === 'dark' ? DARK : LIGHT
  const primary = brandColor && HEX.test(brandColor) ? brandColor : DEFAULT_BRAND
  // Brand gradient for the primary CTAs — derived from the shop's own brand_color so the
  // redesign stays per-shop tinted (Tier 1) in both modes. Lighter top-left → brand bottom-right.
  const gradient = `linear-gradient(140deg, ${lighten(primary, 0.14)}, ${primary})`
  const ctaShadow = '0 8px 18px -7px rgba(120,52,20,0.55)'
  return createTheme({
    palette: {
      mode,
      primary: { main: primary },
      secondary: { main: YOLK },
      success: { main: OLIVE },
      background: { default: t.bg, paper: t.paper },
      text: { primary: t.text, secondary: t.text2 },
      divider: t.divider,
    },
    typography: {
      fontFamily: '"Mulish", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
      // Every heading is the hearty slab serif; Mulish carries the body.
      h1: { fontFamily: SLAB, fontWeight: 700 },
      h2: { fontFamily: SLAB, fontWeight: 700 },
      h3: { fontFamily: SLAB, fontWeight: 700 },
      h4: { fontFamily: SLAB, fontWeight: 700 },
      h5: { fontFamily: SLAB, fontWeight: 700 },
      h6: { fontFamily: SLAB, fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 700 },
    },
    shape: { borderRadius: 16 },
    components: {
      // Flat charcoal surfaces in dark — kill MUI's elevation overlay tint on Paper/Card.
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiAppBar: { styleOverrides: { colorDefault: { backgroundColor: t.paper } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: t.btn, paddingLeft: 20, paddingRight: 20 },
          sizeLarge: { paddingTop: 13, paddingBottom: 13, fontSize: '1rem', borderRadius: t.btnLg },
          // Primary CTAs are the brand gradient with a soft tinted shadow (disableElevation
          // kills the default grey box-shadow so only this one shows).
          containedPrimary: {
            backgroundImage: gradient,
            boxShadow: ctaShadow,
            '&:hover': { backgroundImage: gradient, filter: 'brightness(1.06)', boxShadow: ctaShadow },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: 16, border: `1px solid ${t.border}`, boxShadow: t.shadow, backgroundImage: 'none' },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 700, fontSize: '0.8125rem' } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 14, backgroundColor: t.paper } } },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { borderRadius: 14, backgroundColor: t.paper, border: '1px solid', borderColor: t.divider },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '12px !important',
            border: 'none',
            '&.Mui-selected': {
              backgroundColor: 'var(--toggle-selected-bg, rgba(139,94,60,0.12))',
            },
          },
        },
      },
    },
  })
}
